import "katex/dist/katex.min.css";

import Latex from "react-latex-next";
import { useEffect, useState } from "react";
import type { FC } from "react";
import { Form } from "react-bootstrap";

import { PracticeFooter } from "./components/PracticeFooter";

import { AnswerPractice, ABCDOddeleneOption, ABCDOddeleneOptionsText } from "components";
import { PracticeSelfEvaluation } from "features";

import { useLazyGetAssignmentAnswersQuery } from "api/generated";
import type { UserAssignmentDetailFragment } from "api/generated";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { getAnswerVariants, getCombinedAnswer, setAnswer, setAssignmentEvaluation } from "store/slices/topicPractice";
import type { IOption } from "store/slices/topicPractice";
import { setIsTimerShown } from "store/slices/timer";

import { evaluateABCD, getSolutionVariant } from "utils/getSolutionVariant";
import { ABCDOddeleneOptionText } from "utils/formatTextForAnalysis";
import { useSavePracticeSelfEvaluation } from "utils/hooks/useSavePracticeSelfEvaluation";
import { eventEmitter, Events } from "utils/events/EventEmitter";
import { processApiError } from "utils/processApiError";
import { useEmptyAssignmentAnswer } from "utils/hooks/useEmptyAssignmentAnswer";
import { AssignmentPrompt } from "components/AssignmentPrompt";

/* cYZzbh1FcF7C1Eq3H7ka */

interface Props {
  data: UserAssignmentDetailFragment;
  onNextClick: () => void;
}

const ABCDOddelene: FC<Props> = ({ data, onNextClick }) => {
  const questionsCount = data.assignment?.assignment?.questions.length;
  const isSelfEvaluation = data.assignment?.scoring?.scoringMethod?.scoringMethod === "sebeopravení";

  const savePracticeSelfEvaluation = useSavePracticeSelfEvaluation();
  const dispatch = useAppDispatch();
  const selected = useAppSelector(getCombinedAnswer(data.id));
  const answerVariants = useAppSelector(getAnswerVariants(data.id));

  const [trigger, { data: answerData, isLoading, error: apiLazyError }] = useLazyGetAssignmentAnswersQuery();
  const [answers, setAnswers] = useState<Array<Array<string | null>> | undefined>(undefined);
  const { setEmptyPracticeAnswer } = useEmptyAssignmentAnswer();

  useEffect(() => {
    dispatch(setAnswer({ assignmentId: data.id, answers: new Array(questionsCount).fill({ value: undefined }) }));
    eventEmitter.dispatch(Events.ONBOARDING_UPDATE);
  }, []);

  useEffect(() => {
    if (apiLazyError === undefined) return;

    processApiError(apiLazyError);

    setEmptyPracticeAnswer(data.id, evaluateAnswers);
    setAnswers([]);
  }, [apiLazyError]);

  useEffect(() => {
    const responseAnswers = answerData?.assignments.map(assignment =>
      assignment.assignment?.questions.map(question => {
        if (data.assignment?.assignment?.isAnswerCaseSensitive === true) return question.correctAnswers;
        return question.correctAnswers
          .filter((answer): answer is string => answer !== null)
          .map(answer => answer.toLocaleLowerCase());
      }),
    );

    if (responseAnswers?.[0] === undefined || responseAnswers[0].length === 0) {
      return;
    }

    dispatch(setIsTimerShown(false));
    dispatch(
      setAssignmentEvaluation({
        assignmentId: data.id,
        status: evaluateABCD(selected, responseAnswers[0]),
        answerVariants: evaluateAnswers(responseAnswers[0]),
      }),
    );
    setAnswers(responseAnswers[0]);
  }, [answerData]);

  const evaluateAnswers = (answers: Array<Array<string | null>>) => {
    if (data.assignment?.assignment === undefined || data.assignment?.assignment === null) return [];

    return data.assignment.assignment.questions.map((_question, qIndex) =>
      getSolutionVariant(
        selected[qIndex].value,
        answers,
        qIndex,
        data.assignment?.assignment?.isAnswerCaseSensitive ?? false,
      ),
    );
  };

  const onSubmit = () => {
    if (data.assignment === undefined || data.assignment === null) return;

    trigger({ assignmentId: data.assignment.id }).catch(error => {
      console.error(error);
    });
  };

  const handleCheck = (index: number, option: IOption) => {
    const copy = [...selected];
    copy[index] = option;
    dispatch(setAnswer({ assignmentId: data.id, answers: copy }));
  };

  const areAnswersFilled = () =>
    selected.length > 0 && selected.every(answer => typeof answer.value === "string" && answer.value.length > 0);

  if (isSelfEvaluation) {
    if (data.assignment === undefined || data.assignment === null) return null;

    return (
      <PracticeSelfEvaluation
        userAssignment={data}
        userAssignmentId={data.id}
        onNextClick={onNextClick}
        saveAnswer={savePracticeSelfEvaluation}
      />
    );
  }

  return (
    <div className="exercise__body">
      <AssignmentPrompt assignment={data.assignment?.assignment} />

      <div>
        {data.assignment?.assignment?.questions.map((question, qIndex) => (
          <div key={qIndex} className={"mt-20px"}>
            <Latex>{question.heading ?? ""}</Latex>
            <ABCDOddeleneOptionsText qIndex={qIndex} options={question.options} />

            {answers === undefined ? (
              <Form className="mt-20px question question--select" {...(qIndex === 0 ? { id: "question" } : {})}>
                <ABCDOddeleneOption
                  qIndex={qIndex}
                  onClick={handleCheck}
                  options={question.options}
                  selected={selected}
                />
              </Form>
            ) : data.assignment === undefined || data.assignment === null ? null : (
              <AnswerPractice
                key={`answer-${qIndex}`}
                index={qIndex}
                assignment={data.assignment}
                type={answerVariants?.[qIndex] ?? "incorrect"}
                userAssignmentId={data.id}
                subject={data.subject?.appName}
              >
                {ABCDOddeleneOptionText(selected[qIndex])}
              </AnswerPractice>
            )}
          </div>
        ))}

        <PracticeFooter
          onSubmit={onSubmit}
          isAnalysis={answers !== undefined}
          goNext={onNextClick}
          isSubmitDisabled={!areAnswersFilled() || isLoading}
        />
      </div>
    </div>
  );
};

export default ABCDOddelene;
